import { Tab, Tabs, Box, Paper, Typography } from "@mui/material";
import { useState } from "react";
import { theme } from "../../Theme/BaseTheme";
import CodalLifeExpiredAsset from "./CodalLifeExpiredAsset";
import MidlifeRehabilitation from "./MidlifeRehabilitation";
import MaintenanceOverdue from "./MaintenanceOverdue";
import UnhealthyAssetReport from "./UnhealthyAssetReport";
import SFRReports from "./SFRReports";
import { useSearchParams } from "react-router-dom";
import {
  Assessment as ReportIcon,
  EventBusy as ExpiredIcon,
  Build as RehabilIcon,
  Schedule as OverdueIcon,
  Warning as DeficiencyIcon,
  Description as TfrIcon,
} from "@mui/icons-material";

enum TabValues {
  CODAL_LIFE_EXPIRED_ASSET = "CODAL_LIFE_EXPIRED_ASSET",
  MIDLIFE_REHABILITATION = "MIDLIFE_REHABILITATION",
  MAINTENANCE_OVERDUE = "MAINTENANCE_OVERDUE",
  MAINTENANCE_DEFICIENCY = "MAINTENANCE_DEFICIENCY",
  SFR_REPORTS = "SFR_REPORTS",
}


const MisReports = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState(searchParams.get("selected_tab") as TabValues ?? TabValues.CODAL_LIFE_EXPIRED_ASSET);
  const handleChange = (_: React.SyntheticEvent, newValue: TabValues) => {
    setState(newValue);
    setSearchParams({ selected_tab: newValue });
  };

  return (
    <Box className="mis-reports-container">
      {/* Page Header */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          padding: '1rem 1.25rem',
          backgroundColor: 'var(--color-white)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-color)',
          marginBottom: '1rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            borderRadius: 'var(--radius-md)',
            background: 'linear-gradient(135deg, var(--primary-50) 0%, var(--primary-100) 100%)',
            color: 'var(--primary-600)',
          }}
        >
          <ReportIcon sx={{ fontSize: 24 }} />
        </Box>
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: '1.25rem',
              color: 'var(--neutral-800)',
              lineHeight: 1.3,
            }}
          >
            MIS Reports
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'var(--neutral-500)',
              fontSize: '0.8125rem',
            }}
          >
            Asset lifecycle and maintenance reports
          </Typography>
        </Box>
      </Paper>

      {/* Tabs and Content Container */}
      <Paper elevation={0} className="mis-report-viewer">
        <Tabs
          variant="scrollable"
          scrollButtons="auto"
          value={state}
          onChange={handleChange}
          aria-label="MIS Reports tabs"
          sx={{
            backgroundColor: 'var(--neutral-50)',
            borderBottom: '1px solid var(--border-color)',
            '& .MuiTabs-indicator': {
              backgroundColor: 'var(--primary-600)',
              height: 3,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'var(--neutral-600)',
              padding: '0.875rem 1.25rem',
              minHeight: 52,
              gap: 0.75,
              '&.Mui-selected': {
                color: 'var(--primary-600)',
                fontWeight: 600,
              },
              '&:hover': {
                backgroundColor: 'var(--neutral-100)',
              },
            },
          }}
        >
          <Tab
            value={TabValues.CODAL_LIFE_EXPIRED_ASSET}
            label="Codal Life Outlived"
            icon={<ExpiredIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
          />
          <Tab
            value={TabValues.MIDLIFE_REHABILITATION}
            label="Midlife Rehabilitation"
            icon={<RehabilIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
          />
          <Tab
            value={TabValues.MAINTENANCE_OVERDUE}
            label="Maintenance Overdue"
            icon={<OverdueIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
          />
          <Tab
            value={TabValues.MAINTENANCE_DEFICIENCY}
            label="Maintenance Deficiency"
            icon={<DeficiencyIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
          />
          <Tab
            value={TabValues.SFR_REPORTS}
            label="TFR Reports"
            icon={<TfrIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
          />
        </Tabs>

        <Box className="report-panel">
          {state === TabValues.CODAL_LIFE_EXPIRED_ASSET && <CodalLifeExpiredAsset />}
          {state === TabValues.MIDLIFE_REHABILITATION && <MidlifeRehabilitation />}
          {state === TabValues.MAINTENANCE_OVERDUE && <MaintenanceOverdue />}
          {state === TabValues.MAINTENANCE_DEFICIENCY && <UnhealthyAssetReport />}
          {state === TabValues.SFR_REPORTS && <SFRReports />}
        </Box>
      </Paper>
    </Box>
  );
};

export default MisReports;
